import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  Chip,
  Link
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Instagram as InstagramIcon,
  Warning as WarningIcon,
  Event as EventIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useBrand } from '../../brands';

interface EventData {
  id: string;
  title: string;
  slug: string;
  date: string;
  location: string;
}

const EmailSuccessPage: React.FC = () => {
  const brand = useBrand();
  const { eventSlug } = useParams<{ eventSlug: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const eventData = location.state?.eventData as EventData;
  const email = location.state?.email as string;

  const supportInstagram = brand.copy.chrome.supportInstagram;
  const supportEmail = brand.copy.chrome.supportEmail;
  const isComedy = brand.key === 'zuadaria';

  const handleBackToEvent = () => {
    if (eventSlug) {
      navigate(`/${eventSlug}`);
    } else {
      navigate('/');
    }
  };

  const handleResendAgain = () => {
    if (eventSlug) {
      navigate(`/${eventSlug}/suporte-email`);
    }
  };

  const handleInstagramContact = () => {
    if (!supportInstagram) return;
    window.open(`https://instagram.com/${supportInstagram}`, '_blank');
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box mb={3}>
        <IconButton
          onClick={handleBackToEvent}
          sx={{ mb: 2 }}
          color="primary"
        >
          <ArrowBackIcon />
        </IconButton>

        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <CheckCircleIcon
            sx={{
              fontSize: 80,
              color: 'success.main',
              mb: 2
            }}
          />

          <Typography variant="h4" component="h1" gutterBottom color="success.main">
            {isComedy ? 'Pronto!' : 'Parabéns!'}
          </Typography>

          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            Seu ingresso já está no seu email
          </Typography>

          <Chip
            icon={<EmailIcon />}
            label={email}
            color="primary"
            variant="outlined"
            sx={{ fontSize: '1rem', py: 2, px: 1 }}
          />
        </Box>
      </Box>

      {eventData && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <EventIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {eventData.title}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {format(new Date(eventData.date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
              </Typography>
            </Box>

            {eventData.location && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {eventData.location}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom color="success.main">
          Email enviado com sucesso!
        </Typography>

        <Typography variant="body1" sx={{ mb: 3 }}>
          Seu email de confirmação com o ingresso em PDF foi enviado para <strong>{email}</strong>.
        </Typography>

        <Box sx={{ bgcolor: 'info.light', p: 2, borderRadius: 2, mb: 3 }}>
          <Typography variant="body2" color="info.contrastText">
            <strong>Dica importante:</strong> Verifique também sua pasta de <strong>spam/lixo eletrônico</strong>.
            Às vezes os emails podem ser direcionados para lá automaticamente.
          </Typography>
        </Box>

        {(supportInstagram || supportEmail) && (
          <>
            <Divider sx={{ my: 3 }} />

            <Box sx={{ bgcolor: 'warning.light', p: 2, borderRadius: 2, mb: 3 }}>
              <Typography variant="body2" color="warning.contrastText">
                <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                <strong>Não encontrou o email?</strong>{' '}
                {supportInstagram
                  ? 'Entre em contato conosco no Instagram:'
                  : 'Entre em contato conosco por email:'}
              </Typography>

              {supportInstagram ? (
                <Button
                  variant="contained"
                  startIcon={<InstagramIcon />}
                  onClick={handleInstagramContact}
                  sx={{
                    mt: 2,
                    bgcolor: '#E4405F',
                    '&:hover': { bgcolor: '#C13584' }
                  }}
                >
                  @{supportInstagram}
                </Button>
              ) : (
                <Typography variant="body2" sx={{ mt: 1 }} color="warning.contrastText">
                  <strong>{supportEmail}</strong>
                </Typography>
              )}
            </Box>
          </>
        )}
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          onClick={handleResendAgain}
          startIcon={<EmailIcon />}
        >
          Reenviar Email
        </Button>

        <Button
          variant="contained"
          onClick={handleBackToEvent}
          startIcon={<EventIcon />}
        >
          Ver Evento
        </Button>
      </Box>

      <Box mt={4} sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {isComedy
            ? `Obrigado por comprar com a ${brand.displayName}!`
            : 'Obrigado por participar do nosso evento!'}
        </Typography>
        {supportInstagram && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Em caso de dúvidas, entre em contato conosco no Instagram{' '}
            <Link
              href={`https://instagram.com/${supportInstagram}`}
              target="_blank"
              rel="noopener"
              sx={{ color: 'primary.main', textDecoration: 'none' }}
            >
              @{supportInstagram}
            </Link>
          </Typography>
        )}
        {!supportInstagram && supportEmail && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Em caso de dúvidas: <strong>{supportEmail}</strong>
          </Typography>
        )}
      </Box>
    </Container>
  );
};

export default EmailSuccessPage;
